import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Stack, TextField, MenuItem, LinearProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import apiService, { authFetch } from '../../services/apiServices';
import { activeWorkers } from '../../utils/workerStatus';
import SendReportEmailDialog from '../common/SendReportEmailDialog';
import { ReportPage, ExportMenu } from './ReportKit';

// Leave figures come from GET /api/reports/leave-balance?include=types — the
// same service as "Balanca e pushimeve". This screen used to sum every holiday
// row ever filed (rejected and pending included, every year), read the types
// through a hard-coded id map that no longer matched `leave_types`, took the
// allowance from settings only and floored "remaining" at zero.
const ANNUAL_ID = 1;
const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const fmtRemain = (v) => (v === null || v === undefined ? '—' : r2(v));

// Leave columns are a part of this report, not a precondition for it: if the
// balance endpoint fails, the salary columns still render.
async function fetchLeaveBalances(year) {
  try {
    const res = await authFetch(`/api/reports/leave-balance?year=${year}&status=active&include=types`);
    return await res.json();
  } catch {
    return null;
  }
}

function toCSV(rows, types) {
  const headers = ['Id', 'Emri', 'Mbiemri', 'Email', 'Telefoni', 'PagaAktuale', 'NetoYTD'];
  types.forEach((lt) => headers.push(`${lt.Name} – përdorur`, `${lt.Name} – mbetur`));
  const lines = [headers.map((h) => `"${h}"`).join(',')];
  for (const r of rows) {
    const values = [r.Id, r.FirstName, r.LastName, r.Email || '', r.Phone || '', r._baseSalary || 0, r._netoYTD || 0];
    types.forEach((lt) => {
      const b = r._leave?.[lt.Id];
      values.push(r2(b?.approvedDays), b?.remainingDays === null || b?.remainingDays === undefined ? '' : r2(b.remainingDays));
    });
    lines.push(values.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','));
  }
  return lines.join('\n');
}

export default function EmployeeSummary() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [branches, setBranches] = useState([]);
  const [filterDept, setFilterDept] = useState('all');
  const [filterBranch, setFilterBranch] = useState('all');
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [emailOpen, setEmailOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoading(true);
      try {
        const [workersRes, deptRes, branchRes, leaveRes] = await Promise.all([
          apiService().get('workers'),
          apiService().get('departments'),
          apiService().get('branches'),
          fetchLeaveBalances(year),
        ]);
        if (!workersRes?.success) return;
        const workers = activeWorkers(workersRes.data);
        if (deptRes?.success) setDepartments(deptRes.data || []);
        if (branchRes?.success) setBranches(branchRes.data || []);

        const leaveByWorker = new Map((leaveRes?.data?.rows || []).map((r) => [String(r.workerId), r]));
        if (mounted) setLeaveTypes(leaveRes?.data?.leaveTypes || []);

        const enriched = await Promise.all(
          workers.map(async (w) => {
            // Salary: latest contract BaseSalary and neto YTD from salary history
            let baseSalary = 0;
            let netoYTD = 0;
            try {
              const cRes = await apiService().query('getWorkerContracts', `id=${w.Id}`);
              if (cRes?.success && cRes.data?.length) {
                const latestC = cRes.data[cRes.data.length - 1];
                baseSalary = Number(latestC?.BaseSalary || 0);
              }
              const sRes = await apiService().query('getWorkerSalary', `workerId=${w.Id}`);
              if (sRes?.success && sRes.data?.length) {
                netoYTD = sRes.data
                  .filter((it) => Number(it?.Year || year) === year)
                  .reduce((acc, it) => acc + Number(it?.neto || it?.Neto || 0), 0);
              }
            } catch (_) { /* ignore */ }

            const lb = leaveByWorker.get(String(w.Id));
            return { ...w, _baseSalary: baseSalary, _netoYTD: netoYTD, _leave: lb?.byType || {} };
          })
        );

        if (mounted) setRows(enriched);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, [year]);

  // Annual always; any other type only when somebody has an allowance for it
  // or used it — otherwise the table grows a column of zeros per type.
  const shownTypes = useMemo(() => leaveTypes.filter((lt) => Number(lt.Id) === ANNUAL_ID
    || rows.some((r) => {
      const b = r._leave?.[lt.Id];
      return b && (b.approvedDays > 0 || b.pendingDays > 0 || (b.hasEntitlement && b.entitlementDays > 0));
    })), [leaveTypes, rows]);

  const onExport = () => {
    const csv = toCSV(filtered, shownTypes);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'employee_summary.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const filtered = useMemo(() => {
    return (rows || []).filter((r) => {
      const deptIds = (r?.DepartmentIds || []).map(String);
      const deptOk = filterDept === 'all' || deptIds.includes(String(filterDept)) || String(r?.Department) === String(filterDept);
      const branchField = r?.BranchId ?? r?.Branch ?? r?.branchId;
      const branchOk = filterBranch === 'all' || String(branchField) === String(filterBranch);
      return deptOk && branchOk;
    });
  }, [rows, filterDept, filterBranch]);

  return (
    <ReportPage>
      <Card>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{t('reports.employeeSummary')}</Typography>
            <Stack direction="row" spacing={1}>
              <ExportMenu onCsv={onExport} onEmail={() => setEmailOpen(true)} disabled={filtered.length === 0} />
            </Stack>
          </Stack>
          {loading && <LinearProgress sx={{ mb: 1 }} />}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }}>
            <TextField select size="small" label={t('report.year')} value={year}
              onChange={(e) => setYear(Number(e.target.value))} sx={{ minWidth: 110 }}>
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
            </TextField>
            <TextField
              select
              size="small"
              label={t('report.department')}
              value={filterDept}
              onChange={(e) => setFilterDept(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="all">{t('report.allDepartments')}</MenuItem>
              {departments.map((d) => {
                const id = d.Id ?? d.id;
                return <MenuItem key={id} value={String(id)}>{d.Name || d.name}</MenuItem>;
              })}
            </TextField>
            <TextField
              select
              size="small"
              label={t('report.branch')}
              value={filterBranch}
              onChange={(e) => setFilterBranch(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="all">{t('report.all')}</MenuItem>
              {branches.map((b) => {
                const id = b.Id ?? b.id;
                return <MenuItem key={id} value={String(id)}>{b.Name || b.name}</MenuItem>;
              })}
            </TextField>
          </Stack>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('report.name')}</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>{t('report.phone')}</TableCell>
                  <TableCell align="right">{t('report.currentSalary')}</TableCell>
                  <TableCell align="right">{t('report.netYTD')}</TableCell>
                  {shownTypes.map((lt) => (
                    <React.Fragment key={lt.Id}>
                      <TableCell align="right">{lt.Name}</TableCell>
                      <TableCell align="right">{lt.Name} – mbetur</TableCell>
                    </React.Fragment>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {(filtered || []).map((r) => (
                  <TableRow key={r.Id}>
                    <TableCell>{r.FirstName} {r.LastName}</TableCell>
                    <TableCell>{r.Email || '—'}</TableCell>
                    <TableCell>{r.Phone || '—'}</TableCell>
                    <TableCell align="right">{Number(r._baseSalary || 0).toFixed(2)}</TableCell>
                    <TableCell align="right">{Number(r._netoYTD || 0).toFixed(2)}</TableCell>
                    {shownTypes.map((lt) => (
                      <React.Fragment key={lt.Id}>
                        <TableCell align="right">{r2(r._leave?.[lt.Id]?.approvedDays)}</TableCell>
                        <TableCell align="right">{fmtRemain(r._leave?.[lt.Id]?.remainingDays)}</TableCell>
                      </React.Fragment>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <SendReportEmailDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        reportTitle={t('reports.employeeSummary')}
        subtitle={`${filtered.length} ${t('report.workers')}`}
        columns={[
          { label: t('report.name'), key: r => `${r.FirstName} ${r.LastName}`, align: 'left' },
          { label: 'Email', key: 'Email', align: 'left' },
          { label: t('report.currentSalary'), key: '_baseSalary', align: 'right', format: 'currency' },
          { label: t('report.netYTD'), key: '_netoYTD', align: 'right', format: 'currency' },
          { label: t('report.annualLeave'), key: r => r2(r._leave?.[ANNUAL_ID]?.approvedDays), align: 'right' },
          { label: t('report.annualLeaveRemaining'), key: r => fmtRemain(r._leave?.[ANNUAL_ID]?.remainingDays), align: 'right' },
        ]}
        rows={filtered}
        workers={filtered}
      />
    </ReportPage>
  );
}
